import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Divider, Typography } from '@mui/material';
import { FilterAlt } from '@mui/icons-material';
import ModalComponent from '../../../../components/ModalComponent';
import DropdownV2 from '../../../../components/DropdownComponentV2';
import ButtonComponent from '../../../../components/ButtonComponent';
import { IDeviceFilterParams } from '../../../../services/Devices/types';

interface ControllersFilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentFilter: IDeviceFilterParams;
  onFilter: (val: Partial<IDeviceFilterParams>) => void;
  activeFilterCount: number;
}

const ControllersFilterModal = ({
  isOpen,
  onClose,
  currentFilter,
  onFilter,
  activeFilterCount,
}: ControllersFilterModalProps) => {
  const { t } = useTranslation();

  const brandOptions = useMemo(
    () => [
      { label: 'Intelbras', value: 'INTELBRAS' },
      { label: 'Control ID', value: 'CONTROLID' },
      { label: 'Hikvision', value: 'HIKVISION' },
      { label: 'TopData', value: 'TOPDATA' },
    ],
    [],
  );

  const onlineOptions = useMemo(
    () => [
      { label: t('controllersPage.filter.onlineOptions.yes'), value: 'TRUE' },
      { label: t('controllersPage.filter.onlineOptions.no'), value: 'FALSE' },
    ],
    [t],
  );

  const functionTypeOptions = useMemo(
    () => [
      { label: t('controllersPage.functionTypeOptions.entry'), value: 'ENTRY' },
      { label: t('controllersPage.functionTypeOptions.exit'), value: 'EXIT' },
      { label: t('controllersPage.functionTypeOptions.common'), value: 'COMMON' },
      { label: t('controllersPage.functionTypeOptions.tag'), value: 'TAG' },
    ],
    [t],
  );

  const brandValue = currentFilter.brands?.[0] ?? '';
  const onlineValue =
    currentFilter.isOnline === true
      ? 'TRUE'
      : currentFilter.isOnline === false
        ? 'FALSE'
        : '';
  const functionTypeValue = currentFilter.functionTypes?.[0] ?? '';

  return (
    <ModalComponent
      isOpen={isOpen}
      onClose={onClose}
      width={480}
      title={
        <Box
          component="span"
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}
        >
          <FilterAlt color="warning" />
          {t('controllersPage.filter.title')}
        </Box>
      }
    >
      <Box sx={{ mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {activeFilterCount > 0
            ? t('controllersPage.filter.activeCount', { count: activeFilterCount })
            : t('controllersPage.filter.noneActive')}
        </Typography>
      </Box>
      <Divider sx={{ mb: 2 }} />

      <DropdownV2
        label={t('controllersPage.filter.brand')}
        onChange={(val) =>
          onFilter({ ...currentFilter, brands: val ? [val] : undefined })
        }
        valueField={'value'}
        options={brandOptions}
        labelField={'label'}
        value={brandValue}
        clearable
      />

      <DropdownV2
        label={t('controllersPage.filter.online')}
        onChange={(val) =>
          onFilter({
            ...currentFilter,
            isOnline: val === 'TRUE' ? true : val === 'FALSE' ? false : undefined,
          })
        }
        valueField={'value'}
        options={onlineOptions}
        labelField={'label'}
        value={onlineValue}
        clearable
      />

      <DropdownV2
        label={t('controllersPage.filter.functionType')}
        onChange={(val) =>
          onFilter({ ...currentFilter, functionTypes: val ? [val] : undefined })
        }
        valueField={'value'}
        options={functionTypeOptions}
        labelField={'label'}
        value={functionTypeValue}
        clearable
      />

      <Divider sx={{ mt: 2, mb: 2 }} />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <ButtonComponent onClick={onClose} size="small" variant="outlined">
          {t('common.close')}
        </ButtonComponent>
      </Box>
    </ModalComponent>
  );
};

export default ControllersFilterModal;
